import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import debounce from 'lodash/debounce'

import useCategoryDataStructure from './useCategoryDataStructure'
import useRecommendedForYou from '../../services/recommendationsAPI/useRecommendedForYou'
import useSimilarItems from '../../services/recommendationsAPI/useSimilarItems'
import { Product } from '../../utils/types/Product'
import { QueryParam, TEXT_SECONDARY_COLOR } from '../../utils/constants'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import { capitalizeFirstLetter } from '../../utils/util'
import SafeTranslate from '../common/SafeTranslate'
import { AnalyticsProductCarouselTypes, ProductCarouselTypeValues } from '../../utils/types/analytics'

// Special cases (dashes in category labels): Map of category URLs to their corresponding labels
const CATEGORY_URL_TO_LABEL: Record<string, string> = {
  'Anti-Slip-Paints': 'Anti-Slip Paints',
  'Acid-Base-Neutralizing-Granular-Sorbent': 'Acid-Base Neutralizing Granular Sorbent',
  'gn-container': 'GN-Container',
  'Cut-Off-Tools': 'Cut-Off Tools',
  'chainmail-cut-resistant-gloves': 'Chainmail Cut-Resistant Gloves',
  'Chainmail-Cut-Resistant-Gloves': 'Chainmail Cut-Resistant Gloves',
  'gn-containers': 'GN-Containers',
  'Hard-Hat-Add-ons': 'Hard Hat Add-ons',
  'Spill-Control-Containers': 'Spill-Control Containers',
  'T-shirts-Polos': 'T-shirts & Polos',
}

type PageType = 'search' | 'category' | 'supplier'

interface UseProductCarouselDataParams {
  productFeed: Product[]
  sliceRange?: [number, number]
}

interface UseProductCarouselDataResult {
  products: Product[]
  isLoading: boolean
  title: string | ReactNode
  carouselType: ProductCarouselTypeValues
}

/**
 * Hook for fetching and managing product carousel data for product feeds
 *
 * @param params Parameters for the hook
 * @returns Object containing products, loading state, and title for the carousel
 */
const useProductFeedCarouselData = (
  params: UseProductCarouselDataParams,
): UseProductCarouselDataResult => {
  const { productFeed, sliceRange = [0, 15] } = params

  const { t } = useTranslation('landing')
  const { parentNodes, selectedCategoryId } = useCategoryDataStructure()
  const { query, pathname, locale: countryAndLangString } = useRouter()

  const { country } = getCountryAndLocaleStrings(countryAndLangString)
  const searchQuery = query[QueryParam.search] || null

  const hasTriggeredRef = useRef<Record<string, boolean>>({})

  // Determine page type based on URL and query parameters
  const pageType = useMemo<PageType>(() => {
    if (searchQuery) return 'search'
    if (pathname.includes('[supplierCode]')) return 'supplier'
    return 'category'
  }, [pathname, query])

  // Generate category filter only when needed
  const categoryFilter = useMemo(() => {
    // Only generate for category and supplier pages
    if (pageType === 'search') return ''

    return parentNodes.reduce((acc, curr) => {
      const url = curr['catalog.url']
      // Check if the URL is in our special cases map
      const categoryLabel = CATEGORY_URL_TO_LABEL[url] || url.replace(/-/g, ' ')
      return `${acc}${acc ? ' > ' : ''}${categoryLabel}`
    }, '')
  }, [pageType, parentNodes?.[parentNodes.length - 1]?.code])

  // Get supplier code only for supplier pages
  const supplierCode = pageType !== 'supplier' || !productFeed.length
    ? undefined
    : productFeed[0]['product.code'].split('-')[0]

  // Get product codes for similar items only on search pages
  const productCodes = productFeed.map((product) => product['product.code']).slice(0, 10)

  const pageSize = 40 // Limit page size to 40 to reduce latency

  // Configure filters based on page type
  const recommendationFilters = useMemo(() => {
    switch (pageType) {
      case 'supplier':
        return {
          supplierName: supplierCode,
          categoryName: categoryFilter,
          pageSize,
        }
      case 'category':
        return {
          categoryName: categoryFilter,
          pageSize,
        }
      default:
        return {
          pageSize,
        }
    }
  }, [pageType, categoryFilter, supplierCode])

  // Category / Supplier page recommendations
  const {
    data: recommendedProducts,
    isLoading: isRecommendedLoading,
    triggerCategoryPageView,
    triggerHomePageView: triggerSupplierShopNoCategoryPageView,
  } = useRecommendedForYou(recommendationFilters)

  // Search results page similar items recommendations
  const {
    data: similarProducts,
    isLoading: isSimilarLoading,
    triggerDetailPageView: triggerSimilarProductsSearchPageView,
  } = useSimilarItems({
    associatedItems: pageType === 'search' ? productCodes : [],
    pageSize,
  })

  const triggerPageSpecificDataFetching = useCallback(() => {
    // Create a stable key that considers actual products being used
    const uniqueKey = `${country}-${pageType}-${searchQuery || ''}-${productCodes.slice(0, 10).join('-')}`

    // Skip duplicate triggers for the same products
    if (hasTriggeredRef.current[uniqueKey]) {
      return
    }

    // Only trigger if we have the required parameters
    const hasRequiredParams = (
      ((pageType === 'search' && searchQuery)
      || (pageType === 'category' && categoryFilter && selectedCategoryId)
      || (pageType === 'supplier' && supplierCode))
      && productCodes.length
    )

    if (!hasRequiredParams) {
      return
    }

    // Execute the appropriate trigger
    if (pageType === 'search') {
      triggerSimilarProductsSearchPageView()
    } else if (pageType === 'category') {
      triggerCategoryPageView()
    } else if (pageType === 'supplier') {
      if (categoryFilter) {
        triggerCategoryPageView()
      } else {
        triggerSupplierShopNoCategoryPageView()
      }
    }

    hasTriggeredRef.current[uniqueKey] = true
  }, [pageType,
    searchQuery,
    categoryFilter,
    supplierCode,
    productCodes,
    selectedCategoryId,
    country,
  ])

  // Debounce to ensure products have loaded to avoid race conditions
  useEffect(() => {
    if (!productFeed.length) {
      return
    }

    const debouncedTrigger = debounce(() => triggerPageSpecificDataFetching(), 250)
    debouncedTrigger()

    // eslint-disable-next-line consistent-return
    return () => debouncedTrigger.cancel()
  }, [productFeed.length, triggerPageSpecificDataFetching])

  // Generate title based on page type
  const title = useMemo(() => {
    if (pageType === 'search' && searchQuery) {
      // Capitalize first letter because in FI search term is the first word,
      // which should always be capitalized
      return (
        <SafeTranslate
          i18nKey="products:People who searched for {{ searchTerm }} also bought"
          components={{ span: <span style={{ color: TEXT_SECONDARY_COLOR }} /> }}
          values={{
            searchTerm: capitalizeFirstLetter(searchQuery.toString()),
          }}
        />
      )
    }

    if (pageType === 'supplier') {
      return t('products:Recommended {{ supplier }} products for you', {
        supplier: productFeed[0].supplier[0]['supplier.label'],
      })
    }

    const category = parentNodes[parentNodes.length - 1]?.label
    const categoryLabel = country === 'fi' ? category?.toLowerCase() : category
    return categoryLabel
      ? t('products:Recommended {{ category }} for you', {
        category: categoryLabel,
      })
      : t('products:Recommended for you')
  }, [searchQuery, parentNodes?.[parentNodes.length - 1]?.label, countryAndLangString, productFeed[0].supplier[0]['supplier.label']])

  // Get products based on page type
  const products = [...(
    pageType === 'search' ? similarProducts : recommendedProducts) || []]
    .slice(...sliceRange)

  // Determine loading state based on page type
  const isLoading = pageType === 'search'
    ? isSimilarLoading
    : isRecommendedLoading

  return {
    products,
    isLoading,
    title,
    carouselType: pageType === 'search' ? AnalyticsProductCarouselTypes.SimilarItems : AnalyticsProductCarouselTypes.RecommendedForYou,
  }
}

export default useProductFeedCarouselData
